import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Category, Language } from "@/types";
import { X, Search } from "lucide-react";

type BooksFilterProps = {
  categories: Category[];
  languages?: Language[];
  filters: {
    search?: string;
    category: string | number;
    access: string;
    hasAudio: boolean;
    language: string;
    contentType?: string;
  };
  searchInput?: string;
  onFilterChange: (name: string, value: string | boolean | number) => void;
  onResetFilters: () => void;
};

const BooksFilter = ({
  categories,
  languages = [],
  filters,
  searchInput,
  onFilterChange,
  onResetFilters,
}: BooksFilterProps) => {
  return (
    <div className="bg-card rounded-lg">
      <div className="flex flex-wrap items-end gap-4 p-4">
        {/* Search Field */}
        <div className="flex-1 min-w-[200px]">
          <Label htmlFor="search" className="text-sm font-medium mb-1.5 block">
            Search
          </Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              id="search"
              placeholder="Search by title or author..."
              className="pl-10"
              value={searchInput !== undefined ? searchInput : filters.search}
              onChange={(e) => onFilterChange("search", e.target.value)}
            />
          </div>
        </div>

        {/* Category Select */}
        <div className="min-w-[150px]">
          <Label
            htmlFor="category"
            className="text-sm font-medium mb-1.5 block"
          >
            Category
          </Label>
          <Select
            value={String(filters.category)}
            onValueChange={(value) => onFilterChange("category", value)}
          >
            <SelectTrigger id="category" className="w-full">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((category) => (
                <SelectItem key={category.id} value={String(category.id)}>
                  <div className="flex flex-col">
                    <span>{category.name}</span>
                    {category.name_mz && (
                      <span className="text-xs text-muted-foreground">
                        {category.name_mz}
                      </span>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Language Select */}
        <div className="min-w-[150px]">
          <Label
            htmlFor="language"
            className="text-sm font-medium mb-1.5 block"
          >
            Language
          </Label>
          <Select
            value={filters.language}
            onValueChange={(value) => onFilterChange("language", value)}
          >
            <SelectTrigger id="language" className="w-full">
              <SelectValue placeholder="All Languages" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Languages</SelectItem>
              {languages.map((language) => (
                <SelectItem key={language.code} value={language.code}>
                  {language.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Content Type Select */}
        <div className="min-w-[140px]">
          <Label
            htmlFor="contentType"
            className="text-sm font-medium mb-1.5 block"
          >
            Content Type
          </Label>
          <Select
            value={filters.contentType || "all"}
            onValueChange={(value) => onFilterChange("contentType", value)}
          >
            <SelectTrigger id="contentType" className="w-full">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="summary">Summaries</SelectItem>
              <SelectItem value="full_book">Full Books</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Options */}
        <div className="min-w-[200px]">
          <Label className="text-sm font-medium mb-1.5 block">
            Options
          </Label>
          <div className="flex items-center gap-4 h-9">
            <div className="flex items-center gap-2">
              <Checkbox
                id="hasAudio"
                checked={filters.hasAudio}
                onCheckedChange={(checked) =>
                  onFilterChange("hasAudio", checked === true)
                }
              />
              <Label htmlFor="hasAudio" className="cursor-pointer text-sm">
                Has Audio
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="isFree"
                checked={filters.access === "free"}
                onCheckedChange={(checked) =>
                  onFilterChange("access", checked ? "free" : "all")
                }
              />
              <Label htmlFor="isFree" className="cursor-pointer text-sm">
                Free
              </Label>
            </div>
          </div>
        </div>

        {/* Reset Button */}
        <div>
          <div className="mb-1.5 block h-5">&nbsp;</div>
          <Button
            variant="outline"
            onClick={onResetFilters}
            size="sm"
            className="h-9"
          >
            <X className="w-4 h-4 mr-1" />
            Reset
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BooksFilter;
